import { useCallback, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLogActivity } from 'api'
import { api } from 'lib/axios'
import { statusOptions } from 'constants/StatusOptions'
import { selectCustomer, updateCustomerField } from 'slices/customer'
import { selectPaymentCards } from 'slices/paymentCards'
import { getUserId } from 'lib/auth/tokenStorage'

const normalizeStatusValue = status => {
  const raw = String(status || '').trim()
  if (!raw) return ''

  const normalized = raw.toLowerCase()
  const matchedStatus = statusOptions.find(option => {
    const optionValue = String(option?.value || '').trim().toLowerCase()
    const optionLabel = String(option?.label || '').trim().toLowerCase()

    return optionValue === normalized || optionLabel === normalized
  })

  return matchedStatus?.value || normalized.replace(/\s+/g, '_')
}

const isAuthAccessStatus = status => {
  return normalizeStatusValue(status) === 'auth_access'
}

export const useAutoStatusChange = () => {
  const dispatch = useDispatch()
  const customer = useSelector(selectCustomer)
  const paymentCards = useSelector(selectPaymentCards)
  const { logApiActivity } = useLogActivity()
  const statusChangeRef = useRef(false)
  const [isStatusChangeLoading, setIsStatusChangeLoading] = useState(false)

  const allCardsPassChecks = useCallback((overridesByCardId = {}) => {
    if (!paymentCards || paymentCards.length === 0) return false

    const cardsWithFiles = paymentCards.filter(
      card => card?.files?.length > 0
    )
    if (cardsWithFiles.length === 0) return false

    return cardsWithFiles.every(card => {
      const cardId = card?.attributes?.id
      const override = cardId ? overridesByCardId[cardId] || {} : {}
      const attrs = { ...(card?.attributes || {}), ...override }
      return (
        attrs.luhn_check === '1' &&
        attrs.bin_check === '1' &&
        attrs.redaction_verified === '1' &&
        attrs.ocr_status === '1'
      )
    })
  }, [paymentCards])

  const checkAndUpdateStatus = useCallback(
    async (overridesByCardId = {}, options = {}) => {
      if (statusChangeRef.current) return false
      if (!customer?.id) return false

      const originalStatus = customer?.status_c
      const originalPreviousStatus = customer?.previous_status_c
      const currentStatus = normalizeStatusValue(originalStatus)
      const previousStatus = normalizeStatusValue(originalPreviousStatus)
      if (!isAuthAccessStatus(currentStatus)) return false
      if (!previousStatus) return false
      if (!allCardsPassChecks(overridesByCardId)) return false

      const nextStatus =
        isAuthAccessStatus(previousStatus) && isAuthAccessStatus(currentStatus)
          ? 'refund_request_created'
          : previousStatus

      statusChangeRef.current = true
      setIsStatusChangeLoading(true)

      const agentId = getUserId() || ''

      const resolvedIsLocked =
        options?.is_locked_c ?? (isAuthAccessStatus(currentStatus) ? 1 : 0)

      const payload = {
        contact_id: customer.id,
        agent_id: agentId,
        status_c: nextStatus,
        status_override: 0,
        method: "auto",
        previous_status_c: currentStatus,
        is_locked_c: resolvedIsLocked
      }

      const rollback = () => {
        dispatch(
          updateCustomerField({
            field: 'status_c',
            value: originalStatus
          })
        )

        dispatch(
          updateCustomerField({
            field: 'previous_status_c',
            value: originalPreviousStatus
          })
        )
      }
      dispatch(
        updateCustomerField({
          field: 'status_c',
          value: nextStatus
        })
      )

      dispatch(
        updateCustomerField({
          field: 'previous_status_c',
          value: currentStatus
        })
      )

      try {
        const response = await logApiActivity({
          contactId: customer.id,
          agentId,
          status_override: 0,
          apiEndpoint: '/Api/V8/custom/customer/set-status-change',
          actionType: 'status_change',
          fieldName: 'status_c',
          oldValue: currentStatus,
          newValue: nextStatus,
          requestPayload: payload,
          apiCall: async () =>
            api.post('/Api/V8/custom/customer/set-status-change', payload)
        })

        const isSuccess = response?.data?.success === true

        if (!isSuccess) {
          rollback()
          return false
        }

        return true
      } catch (error) {
        console.error('Error auto-updating status from Auth Access:', error)
        rollback()
        return false
      } finally {
        setIsStatusChangeLoading(false)
        statusChangeRef.current = false
      }
    },
    [
      customer?.id,
      customer?.status_c,
      customer?.previous_status_c,
      allCardsPassChecks,
      dispatch,
      logApiActivity
    ]
  )

  const resetRef = useCallback(() => {
    statusChangeRef.current = false
  }, [])

  return {
    checkAndUpdateStatus,
    allCardsPassChecks,
    isStatusChangeLoading,
    resetRef
  }
}
